import { css } from '@emotion/core'
import { graphql } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import parse from 'html-react-parser'
import React from 'react'

import { AuthorAvatar } from '../../components/AuthorAvatar'
import { AutoCarousel } from '../../components/AutoCarousel'
import Block from '../../components/Block'
import Body from '../../components/Body'
import { CardsNavigation } from '../../components/CardsNavigation'
import LearnArticleCard from '../../components/content-management/product-learn/LearnArticleCard'
import { LearnCategoriesMenu } from '../../components/content-management/product-learn/LearnCategoriesMenu'
import CTAMain from '../../components/ctas/CTAMain'
import Divider from '../../components/Divider'
import Footer from '../../components/Footer'
import Header from '../../components/Header'
import Layout from '../../components/Layout'
import { Link } from '../../components/Link'
import { LoadingSpinner } from '../../components/LoadingComponent'
import ScrollToTop from '../../components/ScrollToTop'
import SearchBar, { SearchResultsBlock } from '../../components/Search'
import SEO from '../../components/SEO'
import { breakpoints, learnShowCardsNavigationBreakPoint } from '../../constants/breakPoints'
import { isMobile, sortLearnArticlesByDate } from '../../helpers/common'
import { getLearnHeaderEntriesLogin } from '../../helpers/header'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'
import { HeaderProps } from '../../interfaces/header'
import { learnBreakPoint, LearnProps } from '../../interfaces/learn'

export const LearnHeader: React.FunctionComponent<HeaderProps> = (props) => {
  return <Header isDark entriesLogin={getLearnHeaderEntriesLogin} {...props} />
}

export const Learn: React.FunctionComponent<LearnProps> = ({
  data: {
    categories,
    learnAuthors,
    allTopArticles: { edges: topArticles = [] },
  },
}) => {
  const [search, setSearch] = React.useState('')
  const debouncedSearch = useDebouncedValue(search)

  const allArticles = categories.edges
    .flatMap(({ node: { categoryArticles, categoryColor } }) =>
      categoryArticles.map((categoryArticle) => ({ ...categoryArticle, categoryColor }))
    )
    .sort((a, b) => sortLearnArticlesByDate(a, b))

  const filteredSearchArticles = React.useMemo(
    () => allArticles.filter(({ title }) => title.toLowerCase().includes(debouncedSearch.toLowerCase())),
    [debouncedSearch]
  )

  const authors = React.useMemo(
    () =>
      learnAuthors.edges.sort(
        (a, b) => (b.node?.authorPublications?.length || 0) - (a.node?.authorPublications?.length || 0)
      ),
    [learnAuthors]
  )

  return (
    <Layout blackBG>
      <SEO
        title={'Product Learn'}
        description={
          'Discover best practices, tips and didactic, high quality content to achieve your maximum level of assertivenes in product management'
        }
      />
      <LearnHeader />
      <Body>
        {isMobile(learnShowCardsNavigationBreakPoint) && (
          <SearchBar
            blackBG
            isSlidingComponent
            search={search}
            setSearch={setSearch}
            placeholder={'Search articles...'}
          />
        )}

        <LearnCategoriesMenu
          isHero
          search={search}
          setSearch={setSearch}
          categories={categories}
          headlineText={'Learn about <br/> Product Management'}
          subheaderText={
            "Everything you ever wanted to learn about product management and more. A series of actionable guides that equip you to become a <br class='hide-on-small-medium hide-on-desktop' /> better product manager at any level of your career."
          }
        />

        <ScrollToTop />

        <div css={carouselStyle}>
          <h2 className={'heading2'}>Featured writers</h2>
          <AutoCarousel
            settings={{ slidesToShow: isMobile(breakpoints.small) ? 2 : isMobile(breakpoints.large) ? 4 : 6 }}
          >
            {authors.map(({ node }) => (
              <AuthorAvatar author={node} key={node.slug} />
            ))}
          </AutoCarousel>
        </div>

        <div id={'learn-cards-navigation-sticky-container-reference'}>
          {isMobile(learnShowCardsNavigationBreakPoint) && (
            <CardsNavigation
              blackBG
              withSearch
              keepSearchOnSmallScreens
              hideUntilSticky
              referenceIdForStickiness={'learn-cards-navigation-sticky-container-reference'}
              search={search}
              setSearch={setSearch}
              stickyLayoutOnBreakPoint
              breakPoint={learnShowCardsNavigationBreakPoint}
              hrefPrefix={'product-learn/category'}
              categories={categories.edges.map(({ node }) => ({
                node: {
                  slug: node.slug,
                  name: node.name.replace('<br />', ''),
                  navigationIcon: {},
                  categoryColor: node.categoryColor,
                },
              }))}
            />
          )}

          {categories.edges.map(
            ({
              node: {
                categoryLogo,
                slug,
                name,
                description: { description },
                categoryColor,
                categoryArticles,
              },
            }) => {
              return (
                <React.Fragment key={slug}>
                  {categoryArticles && (
                    <>
                      <Block
                        columns={2}
                        columnsTemplate={'1fr 2fr'}
                        alignContent={'flex-start'}
                        css={learnAboutBlockStyle(learnBreakPoint)}
                      >
                        <Link
                          href={`/product-learn/category/${slug}/`}
                          className={'white-text opacity-on-hover'}
                          css={leftColumnStyle(learnBreakPoint)}
                        >
                          <GatsbyImage
                            image={categoryLogo.gatsbyImageData}
                            alt={categoryLogo.title}
                            imgStyle={{ width: '48px' }}
                          />
                          <h3 className={'heading3'}>
                            Learn about <br /> {parse(name.toLowerCase().replace('ux ', 'UX '))}
                          </h3>
                        </Link>
                        <div css={rightColumnStyle}>
                          <div className={'subtitle'}>{parse(description)}</div>
                          <Link
                            href={`/product-learn/category/${slug}/`}
                            className={'subtitle'}
                            css={linkStyle(categoryColor)}
                            withArrow
                            arrowColor={categoryColor}
                          >
                            See all {name.replace('<br />', '').toLowerCase().replace('ux ', 'UX ')} resources
                          </Link>
                          <Block
                            columns={2}
                            rowGap={1}
                            css={LearnArticleCardsStyle}
                            alignContent={'stretch'}
                            noBreakForTablet
                          >
                            {categoryArticles
                              .sort((a, b) => sortLearnArticlesByDate(a, b))
                              .slice(0, 4)
                              .map(({ soon, author, abstract: { abstract }, title, slug, createdAt }) => (
                                <LearnArticleCard
                                  key={slug}
                                  isSoon={soon}
                                  categoryColor={categoryColor}
                                  author={author}
                                  description={abstract}
                                  title={title}
                                  articleUrl={slug}
                                  createdAt={createdAt}
                                />
                              ))}
                          </Block>
                        </div>
                      </Block>
                      <Divider size={8} color={'rgba(136, 146, 158, 0.4)'} />
                    </>
                  )}
                </React.Fragment>
              )
            }
          )}
        </div>

        {!!topArticles.length && (
          <React.Fragment>
            <h2 className={'heading2'}>💎 Top posts</h2>
            <Block
              columns={2}
              columnsTemplate={'2fr 1fr'}
              alignContent={'stretch'}
              css={topArticlesStyle(learnBreakPoint)}
            >
              <Block columns={2} alignContent={'stretch'} noBreakForTablet>
                <LearnArticleCard
                  title={topArticles[0].node.title}
                  description={topArticles[0].node.abstract.abstract}
                  author={topArticles[0].node.author}
                  categoryColor={topArticles[0].node.category.categoryColor}
                  isSoon={topArticles[0].node.soon}
                  articleUrl={topArticles[0].node.slug}
                  createdAt={topArticles[0].node.createdAt}
                />
                <LearnArticleCard
                  title={topArticles[1].node.title}
                  description={topArticles[1].node.abstract.abstract}
                  categoryColor={topArticles[1].node.category.categoryColor}
                  author={topArticles[1].node.author}
                  isSoon={topArticles[1].node.soon}
                  articleUrl={topArticles[1].node.slug}
                  createdAt={topArticles[1].node.createdAt}
                />
              </Block>
              <Block rowGap={1} css={topArticlesSmallStyle(learnBreakPoint)}>
                {topArticles.slice(2, 5).map(({ node }) => (
                  <LearnArticleCard
                    key={node.slug}
                    isSmall
                    title={node.title}
                    description={node.abstract.abstract}
                    categoryColor={node.category.categoryColor}
                    author={node.author}
                    isSoon={node.soon}
                    articleUrl={node.slug}
                    logoSrc={node.category.categoryWhiteLogo}
                    createdAt={node.createdAt}
                  />
                ))}
              </Block>
            </Block>
          </React.Fragment>
        )}

        {/* for desktop: <SearchBar /> would be overlapped => no spinner */}
        {isMobile(learnShowCardsNavigationBreakPoint) && search && <LoadingSpinner blackBG />}

        {/* slide-up for search results (overlay) */}
        <SearchResultsBlock
          blackBG
          withSearchBar={!isMobile(learnShowCardsNavigationBreakPoint)}
          search={search}
          setSearch={setSearch}
          debouncedSearch={debouncedSearch}
          resultsArray={filteredSearchArticles}
          resultsBlockContent={filteredSearchArticles.map((filteredArticle) => (
            <LearnArticleCard
              key={filteredArticle.slug}
              isSoon={filteredArticle.soon}
              categoryColor={filteredArticle.categoryColor}
              author={filteredArticle.author}
              description={filteredArticle.abstract.abstract}
              title={filteredArticle.title}
              articleUrl={filteredArticle.slug}
              createdAt={filteredArticle.createdAt}
            />
          ))}
          breakPoint={learnBreakPoint}
          cssStyles={learnSearchResultsBlockStyle}
        />

        <CTAMain />
      </Body>
      <Footer blackBG />
    </Layout>
  )
}

export default Learn

export const query = graphql`
  query ProductLearnPage {
    categories: allContentfulLearnCategory(sort: { menuOrder: ASC }) {
      edges {
        node {
          id
          name
          categoryColor
          description {
            description
          }
          categoryLogo {
            title
            gatsbyImageData(width: 48, height: 48)
          }
          slug

          categoryArticles: learn_article {
            soon
            slug
            title
            popular
            abstract {
              abstract
            }
            author {
              authorPhoto {
                gatsbyImageData(width: 48)
              }
              companyName
              jobTitle
              name
              slug
            }
            createdAt
          }
        }
      }
    }

    learnAuthors: allContentfulLearnAuthor(
      filter: { learn_article: { elemMatch: { content: { raw: { ne: null } } } } }
    ) {
      edges {
        node {
          name
          slug
          companyName
          jobTitle
          authorPhoto {
            gatsbyImageData(width: 80)
          }
          authorPublications: learn_article {
            id
          }
        }
      }
    }

    allTopArticles: allContentfulLearnArticle(limit: 5, filter: { popular: { eq: true } }, sort: { createdAt: DESC }) {
      edges {
        node {
          soon
          slug
          title
          createdAt
          popular

          abstract {
            abstract
          }

          author {
            name
            slug
            authorPhoto {
              gatsbyImageData(width: 48)
            }
          }

          category {
            categoryColor
            categoryWhiteLogo {
              title
              gatsbyImageData(height: 32)
            }
          }
        }
      }
    }
  }
`

const carouselStyle = css`
  margin-bottom: 10rem;

  @media screen and (max-width: ${breakpoints.large}px) {
    margin-bottom: 4rem;
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    .heading2 {
      text-align: center !important;
    }
  }
`

const learnAboutBlockStyle = (breakpoint: number) => css`
  @media screen and (max-width: ${breakpoint}px) {
    grid-template-columns: 1fr;
    row-gap: 1.5rem;
  }
`

const leftColumnStyle = (breakpoint: number) => css`
  display: grid;
  grid-template-columns: 3rem 1fr;
  align-items: flex-start;
  column-gap: 1.5rem;

  @media screen and (min-width: ${breakpoint + 1}px) {
    .gatsby-image-wrapper {
      margin-top: 0.3rem;
    }
  }

  @media screen and (max-width: ${breakpoint}px) {
    pointer-events: none;
    grid-template-columns: 1fr;
    row-gap: 1rem;
  }
`

const rightColumnStyle = css`
  & > :first-child {
    margin-top: 0;
  }

  .subtitle {
    max-width: 40rem;
  }
`

const linkStyle = (categoryColor: string) => css`
  color: ${categoryColor};
`

const LearnArticleCardsStyle = css`
  margin-top: 3rem;
`

const topArticlesStyle = (breakpoint: number) => css`
  @media screen and (min-width: ${breakpoints.small + 1}px) and (max-width: ${breakpoint}px) {
    grid-template-columns: 1fr;
  }
`

const topArticlesSmallStyle = (breakpoint: number) => css`
  @media screen and (min-width: ${breakpoints.small + 1}px) and (max-width: ${breakpoint}px) {
    grid-template-columns: 1fr 1fr;
  }
`

// export for LearnCategory.tsx, LearnSubcategory.tsx
export const learnSearchResultsBlockStyle = css`
  @media screen and (max-width: ${learnShowCardsNavigationBreakPoint}px) {
    margin-top: 4rem;
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    margin-top: 2rem;
  }
`
